import styled, { createGlobalStyle } from 'styled-components';

import { COLORS, MOBILE } from '../../../../theme/tokens';
import { Text } from '../../../../components/Text/Text';
import { blobLayer, ringLayer, veilLayer } from '../../generating.styles';

/**
 * Registers the arc angle once (`createGlobalStyle` dedupes across every wash on
 * the page). On engines that ignore animating a registered property the arc sits
 * static, which is the same fallback the blog list takes.
 */
export const GlowAngleProperty = createGlobalStyle`
  @property --blog-write-glow-angle {
    syntax: '<angle>';
    inherits: false;
    initial-value: 0deg;
  }
`;

/**
 * The overlay itself. It fills its host, which must be `position: relative` and
 * establish a stacking context, and it never answers a hit test — the host's own
 * `disabled` attributes are what stop the author typing into a field being
 * written, not a transparent sheet over the top.
 */
export const Root = styled.div<{ $page: boolean }>`
  position: ${(p) => (p.$page ? 'fixed' : 'absolute')};
  inset: 0;
  z-index: ${(p) => (p.$page ? 60 : 2)};
  border-radius: ${(p) => (p.$page ? '0' : 'inherit')};
  overflow: hidden;
  pointer-events: ${(p) => (p.$page ? 'auto' : 'none')};
  display: ${(p) => (p.$page ? 'grid' : 'block')};
  place-items: center;
`;

export const Veil = styled.span<{ $page: boolean }>`
  ${veilLayer}
  opacity: ${(p) => (p.$page ? 0.72 : 0.35)};
`;

export const Blobs = styled.span<{ $blur: number; $opacity: number }>`
  ${(p) => blobLayer(p.$blur, p.$opacity)}
`;

/** The travelling rim. Skipped on the page-wide wash: there is no card edge to trace. */
export const Ring = styled.span`
  ${ringLayer}
`;

/**
 * The status line, for assistive tech only. The wash carries no information a
 * screen reader can use, so the fact of it is announced in words instead.
 */
export const SrOnly = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
`;

/* ── Test-only, for GeneratingWash.story ── */

/** Stand-in page the story mounts the wash against. */
export const StoryHost = styled.div`
  width: 460px;
  padding: 24px;
`;

/** Stand-in field, so the overlay has something to be written over. */
export const StoryField = styled.div`
  position: relative;
  isolation: isolate;
  border-radius: 12px;
  border: 1px solid ${COLORS.panelBorder};
  padding: 14px 16px;
  color: ${COLORS.text};
`;

/** What is being written, said once in the middle of the page-wide wash. */
export const Caption = styled(Text)`
  position: relative;
  z-index: 1;
  max-width: 320px;
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.5;
  color: ${COLORS.textStrong};
  text-shadow: 0 1px 12px ${COLORS.pageBg};

  ${MOBILE} {
    max-width: 260px;
    font-size: 15px;
  }
`;
